@if (authLoading()) {
  <div class="loading-container">
    <fancy-spinner [size]="50" color="primary"></fancy-spinner>
    <p>Verificando autenticación...</p>
  </div>
} @else if (!isLoginPage() && (platformAdmin() || (currentUser() && businessContext()))) {
  <mat-sidenav-container class="sidenav-container">
    <mat-sidenav
      #drawer
      class="sidenav"
      fixedInViewport
      [attr.role]="isHandset() ? 'dialog' : 'navigation'"
      [mode]="isHandset() ? 'over' : 'side'"
      [opened]="!isHandset() && (currentUser() !== null || platformAdmin() !== null)"
    >
      <mat-toolbar color="primary">
        <span>Menú</span>
      </mat-toolbar>

      @if (currentUser() || platformAdmin()) {
        <mat-nav-list>
          @for (item of getFilteredNavItems(); track item.route) {
            @if (item.children && item.children.length > 0) {
              <!-- Item with submenu - Custom accordion -->
              <div class="nav-accordion">
                <div
                  class="nav-accordion__header"
                  [class.nav-accordion__header--expanded]="item.expanded"
                  (click)="toggleSubmenu(item)"
                >
                  <span class="nav-accordion__icon-wrapper">
                    <mat-icon>{{ item.icon }}</mat-icon>
                  </span>
                  <span class="nav-accordion__label">{{ item.label }}</span>
                  <mat-icon class="nav-accordion__arrow">
                    {{ item.expanded ? 'expand_less' : 'expand_more' }}
                  </mat-icon>
                </div>

                @if (item.expanded) {
                  <div class="nav-accordion__content">
                    <!-- Child links (includes parent route as first child) -->
                    @for (child of item.children; track child.route) {
                      <a
                        class="nav-accordion__item"
                        [routerLink]="child.route"
                        routerLinkActive="nav-accordion__item--active"
                        (click)="drawer.close()"
                      >
                        <span>{{ child.label }}</span>
                      </a>
                    }
                  </div>
                }
              </div>
            } @else {
              <!-- Regular item without submenu -->
              <a
                mat-list-item
                [routerLink]="item.route"
                routerLinkActive="active-link"
                (click)="drawer.close()"
                class="sidenav__nav-item"
              >
                <mat-icon matListItemIcon class="sidenav__nav-icon">{{ item.icon }}</mat-icon>
                <span matListItemTitle>{{ item.label }}</span>
              </a>
            }
          }
        </mat-nav-list>
      }
    </mat-sidenav>

    <mat-sidenav-content>
      @if (currentUser() || platformAdmin()) {
        <mat-toolbar color="primary" class="toolbar">
          @if (currentUser() || platformAdmin()) {
            <button
              type="button"
              aria-label="Toggle sidenav"
              mat-icon-button
              (click)="drawer.toggle()"
            >
              <mat-icon>menu</mat-icon>
            </button>
          }

          <!-- POS-specific controls (left side) - Only show when cashier session is active -->
          @if (posToolbarService.isPosRoute() && posToolbarService.hasCashierSession()) {
            <!-- Product Search -->
            <div class="toolbar__pos-search">
              <fancy-input
                [type]="'text'"
                [placeholder]="'Buscar productos...'"
                [prefixIcon]="'search'"
                [clearable]="true"
                [size]="'sm'"
                [glow]="false"
                class="toolbar__search-input"
                [formControl]="posToolbarService.searchControl"
              ></fancy-input>
            </div>

            <!-- View Mode Toggle -->
            <div class="toolbar__view-toggle">
              <fancy-button-toggle
                [options]="viewModeOptions"
                [value]="posToolbarService.viewMode()"
                [showLabels]="false"
                [size]="'sm'"
                (valueChange)="onViewModeChange($event)"
              ></fancy-button-toggle>
            </div>
          } @else if (!posToolbarService.isPosRoute()) {
            <span class="logo">
              {{ businessContext()?.business?.name }}
            </span>
          }

          <span class="spacer"></span>

          <!-- POS Client Selection (center) - Only on desktop; mobile uses client-selector-sticky -->
          @if (
            posToolbarService.isPosRoute() && posToolbarService.hasCashierSession() && !isHandset()
          ) {
            <div class="toolbar__client-selection">
              <!-- Cliente seleccionado (no predeterminado) -->
              @if (
                !posToolbarService.isDefaultClient() && posToolbarService.selectedClient();
                as client
              ) {
                <fancy-chip
                  [variant]="'primary'"
                  [icon]="'person'"
                  [removable]="true"
                  (removed)="posToolbarService.onClientRemove?.()"
                >
                  {{ truncateClientName(client.name) }}
                </fancy-chip>
                <button
                  class="toolbar__edit-client-btn"
                  (click)="posToolbarService.onEditClient?.(client)"
                >
                  <mat-icon>edit</mat-icon>
                </button>
              }

              <!-- Cliente predeterminado seleccionado - mostrar chip no removible -->
              @if (
                posToolbarService.isDefaultClient() && posToolbarService.selectedClient();
                as client
              ) {
                <fancy-chip [variant]="'default'" [icon]="'person'" [removable]="false">
                  {{ truncateClientName(client.name) }}
                </fancy-chip>
              }

              <!-- Autocomplete para buscar y seleccionar cliente (wide desktop only, >1120px) -->
              @if (posToolbarService.selectedClient() && isDesktopWide()) {
                <fancy-autocomplete
                  [placeholder]="'Buscar cliente...'"
                  [prefixIcon]="'person'"
                  [options]="posToolbarService.filteredClients()"
                  [displayWith]="displayClientName"
                  [disableFilter]="true"
                  [size]="'sm'"
                  [glow]="false"
                  [clearAfterSelect]="true"
                  [formControl]="posToolbarService.clientSearchControl"
                  (optionSelected)="posToolbarService.onClientSelect?.($event)"
                  class="toolbar__client-autocomplete"
                >
                  <ng-template #optionTemplate let-client>
                    <div class="toolbar__client-option">
                      <mat-icon>person</mat-icon>
                      <div class="toolbar__client-info">
                        <span class="toolbar__client-name">{{ client.name }}</span>
                        <span class="toolbar__client-phone">{{ client.phone }}</span>
                      </div>
                    </div>
                  </ng-template>
                </fancy-autocomplete>
              }

              <!-- Botón agregar cliente (siempre visible) -->
              <fancy-button
                [variant]="'ghost'"
                [size]="'sm'"
                [icon]="'person_add'"
                [matTooltip]="'Agregar cliente rápido'"
                (clicked)="posToolbarService.onAddQuickClient?.()"
                class="toolbar__client-add-btn"
              ></fancy-button>
            </div>

            <!-- Client Button for Tablets (768px-1120px) -->
            <div class="toolbar__client-button">
              <button
                [class.active]="clientPanelOpen()"
                [matTooltip]="'Seleccionar cliente'"
                (click)="toggleClientPanel()"
              >
                <mat-icon>person</mat-icon>
              </button>
            </div>

            <!-- Dropdown Panel for Tablets -->
            @if (clientPanelOpen()) {
              <div class="toolbar__client-panel">
                <client-selector-panel
                  [selectedClient]="posToolbarService.selectedClient()"
                  [isDefaultClient]="!!posToolbarService.isDefaultClient()"
                  [filteredClients]="posToolbarService.filteredClients()"
                  [searchControl]="posToolbarService.clientSearchControl"
                  (clientSelected)="onClientSelectFromPanel($event)"
                  (clientRemoved)="posToolbarService.onClientRemove?.()"
                  (addQuickClient)="posToolbarService.onAddQuickClient?.()"
                ></client-selector-panel>
              </div>
            }
          }

          <span class="spacer"></span>

          @if (currentUser(); as user) {
            <!-- Business name -->
            @if (posToolbarService.isPosRoute()) {
              <div class="business-name">
                {{ businessContext()?.business?.name }}
              </div>
            } @else if (!viewport.isXs()) {
              <div class="business-name">Impulsado por Pay Caja</div>
            }

            <!-- Transfer Notifications Bell -->
            <app-transfer-notifications></app-transfer-notifications>

            <!-- User menu -->
            <button mat-icon-button [matMenuTriggerFor]="userMenu">
              <mat-icon>account_circle</mat-icon>
            </button>

            <mat-menu #userMenu="matMenu">
              <div class="user-info" mat-menu-item disabled>
                <div class="user-name">{{ user.displayName || user.email }}</div>
                @if (userRole()) {
                  <div class="user-role">{{ getRoleLabel(userRole() || '') }}</div>
                }
              </div>
              @if (user.businesses && user.businesses.length > 1) {
                <button mat-menu-item routerLink="/select-business">
                  <mat-icon>business</mat-icon>
                  <span>Cambiar negocio</span>
                </button>
              }
              <mat-divider></mat-divider>
              <button mat-menu-item routerLink="/profile">
                <mat-icon>person</mat-icon>
                <span>Perfil</span>
              </button>
              <button mat-menu-item (click)="logout()">
                <mat-icon>exit_to_app</mat-icon>
                <span>Cerrar sesión</span>
              </button>
            </mat-menu>
          } @else if (platformAdmin(); as admin) {
            <!-- Platform Admin menu -->
            <div class="admin-badge">
              <mat-icon>admin_panel_settings</mat-icon>
              <span>Platform Admin</span>
            </div>

            <button mat-icon-button [matMenuTriggerFor]="adminMenu">
              <mat-icon>account_circle</mat-icon>
            </button>

            <mat-menu #adminMenu="matMenu">
              <div class="user-info" mat-menu-item disabled>
                <div class="user-name">{{ admin.displayName }}</div>
                <div class="user-role">{{ getRoleLabel(admin.role) }}</div>
              </div>
              <mat-divider></mat-divider>
              <button mat-menu-item (click)="logout()">
                <mat-icon>exit_to_app</mat-icon>
                <span>Cerrar sesión</span>
              </button>
            </mat-menu>
          }
        </mat-toolbar>

        <app-past-due-banner />
      }

      <main class="main-content">
        <router-outlet></router-outlet>
      </main>
    </mat-sidenav-content>
  </mat-sidenav-container>
} @else {
  <router-outlet></router-outlet>
}
